/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use an form by id.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type the state of a request. 
 */
import { IRequestState, RequestState } from '@ngt/request-utilities';

/*
 * Used to type actions.
 */
import { ActionCreators } from 'immer-reducer';

/*
 * Used to get access to dispatch to dispatch actions to the store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to get access to backend types.
 */
import * as Dtos from '../../api/dtos';

/*
 * used to type actions.
 */
import { FormReducer, useFormSelector, formSelectors, IFormStore } from '../../store/modules/data/form';

/*
 * Used to remove the first parameter from the base action creator function type.
 */
import { OmitFirstArg } from '../../utilities/OmitArgs';

/*
 * Used to type action creators with first parameter bound to a static. 
 */
import { BoundActionCreator } from '../../utilities/BoundActionCreator';

/*
 * Used to bind action creators to the store but keep their assigned type property.
 */
import bindActionCreatorsWithType from '../../utilities/bindActionCreatorsWithType';

/*
 * Used to get online patient management settings from context.
 */
import FormsContext from '../../contexts/FormsContext';
import { TypedFunction } from '../../utilities/functionTypes';
import useAsyncFunction from '../useAsyncFunction';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export interface IUseFormByIdActions {
    load: BoundActionCreator<OmitFirstArg<ActionCreators<typeof FormReducer>['loadById']>>;
    save: BoundActionCreator<OmitFirstArg<ActionCreators<typeof FormReducer>['saveById']>>;
    clear: BoundActionCreator<OmitFirstArg<ActionCreators<typeof FormReducer>['clearById']>>;

    asyncSave: TypedFunction<Parameters<BoundActionCreator<OmitFirstArg<ActionCreators<typeof FormReducer>['clearById']>>>, Promise<Dtos.IForm | undefined>>
}

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const useFormById = <FormType extends Dtos.IForm>(
    formDefinitionIdentifier: string | number,
    id: number | null,
    autoLoad?: boolean,
    allowCreate?: boolean
): [
    FormType | null,
    IRequestState<Dtos.ResponseStatus>,
    IRequestState<Dtos.ResponseStatus>,
    IUseFormByIdActions
] => {
    const dispatch = useDispatch();

    const onlinePatientManagement = React.useContext(FormsContext);

    const formPropertyName = React.useMemo(() => {
        if (typeof formDefinitionIdentifier === 'number') {
            return onlinePatientManagement.formMetadata.find(fm => fm.formDefinitionId === formDefinitionIdentifier)?.propertyName;
        }

        return onlinePatientManagement.formMetadata.find(fm => fm.formDefinitionCode === formDefinitionIdentifier)?.propertyName;
    }, [onlinePatientManagement.formMetadata, formDefinitionIdentifier]);

    if (!formPropertyName) {
        throw new Error("Form Metadata not found for provided Form Definition Identifier.");
    }

    const unboundActions = React.useMemo(() => {
        return onlinePatientManagement.formReducerRegistry.getActions(formPropertyName);
    }, [onlinePatientManagement.formReducerRegistry, formPropertyName]);

    if (!unboundActions) {
        throw new Error("Form Reducer not found for provided Form Definition Identifier.");
    }

    const unboundAsyncSave = useAsyncFunction(unboundActions.saveById, unboundActions.saveByIdSuccess, unboundActions.saveByIdFailure);

    const actions: IUseFormByIdActions = React.useMemo(() => {
        const load = () => unboundActions.loadById(id, allowCreate);
        load.type = unboundActions.loadById.type;

        const save = (form?: Dtos.IForm) => unboundActions.saveById(id, form);
        save.type = unboundActions.saveById.type;

        const clear = () => unboundActions.clearById(id);
        clear.type = unboundActions.clearById.type;

        const asyncSave = async (form?: Dtos.IForm) => {
            const [, savedForm] = await unboundAsyncSave([id, form]) as any;

            return savedForm;
        };

        return {
            ...bindActionCreatorsWithType({
                load,
                save,
                clear
            }, dispatch),
            asyncSave
        };
    }, [unboundActions, id, dispatch, unboundAsyncSave]);

    const formSelector = React.useCallback((state: IFormStore) => {
        return formSelectors.formById(state, formPropertyName, id);
    }, [formSelectors.formById, formPropertyName, id]);

    const loadStateSelector = React.useCallback((state: IFormStore) => {
        return formSelectors.loadStateById(state, formPropertyName, id);
    }, [formSelectors.loadStateById, formPropertyName, id]);

    const saveStateSelector = React.useCallback((state: IFormStore) => {
        return formSelectors.saveStateById(state, formPropertyName, id);
    }, [formSelectors.saveStateById, formPropertyName, id]);

    const form = useFormSelector(formSelector);

    const loadState = useFormSelector(loadStateSelector);

    const saveState = useFormSelector(saveStateSelector);

    React.useEffect(() => {
        if (autoLoad) {
            actions.load();

            return () => {
                actions.clear();
            };
        }

        return () => { };
    }, [autoLoad, id]);

    return [
        form as FormType | null,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useFormById;